import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  CustomProductOptionConfigType,
  IProduct,
  IProductFileWithFile,
} from "@evennt-tech/api-v2-core";
import { shallow } from "zustand/shallow";
import { customProductOptionConfigRepository } from "api/customProductOptionConfig";
import {
  parseCustomProductOptionConfig,
  unpackInlineProductConfigs,
} from "store/custom-product-option/utils/helpers";
import { ProductConfig } from "ts/api/product";
import { registerStore } from "store/utils/reset-store.utils";
import { collectProductFiles } from "tools/product/collectProductFiles";

type CustomProductOptionConfigsByProduct = Map<
  string,
  { configs: ProductConfig[]; files: IProductFileWithFile[] }
>;
export type ProductConfigsBySupplier = Map<
  string,
  CustomProductOptionConfigsByProduct
>;

export type State = {
  productConfigsBySupplier: ProductConfigsBySupplier;
};

type LoadCustomConfigsParams = {
  productId: string;
  supplierCompanyId: string;
  showId: string;
};

type Action = {
  setFromProducts: (
    products: IProduct[],
    showId: string
  ) => ProductConfigsBySupplier;
  cacheRecommendedProducts: (
    product: IProduct,
    showId: string
  ) => ProductConfigsBySupplier;
  /**
   * Loads every enabled CUSTOM config of a product whose matrix the product
   * list left out and stores them next to its DEFAULT config. The next
   * `setFromProducts` for that product drops them again (they may be stale
   * by then); views that need them call this again.
   */
  loadCustomConfigs: (
    params: LoadCustomConfigsParams
  ) => Promise<ProductConfig[]>;
  /** Adds CUSTOM configs fetched by id (cart lines) next to what is stored. */
  appendCustomConfigs: (params: {
    productId: string;
    supplierCompanyId: string;
    configs: ProductConfig[];
  }) => void;
  reset: () => void;
};

type CustomProductOptionShopStore = State & Action;

export const customProductOptionShopStore = create(
  devtools<CustomProductOptionShopStore>((set, get) => ({
    productConfigsBySupplier: new Map(),

    setFromProducts: (products, showId) => {
      const { productConfigsBySupplier } = get();
      const next: ProductConfigsBySupplier = new Map(productConfigsBySupplier);
      let hasUnpackedConfigs = false;

      products.forEach((product) => {
        if (!product?.supplierCompanyId) {
          return;
        }

        const configs = unpackInlineProductConfigs(
          product.customProductOptionConfigs,
          showId
        );

        if (!configs.length) {
          return;
        }

        const configsByProduct: CustomProductOptionConfigsByProduct = new Map(
          next.get(product.supplierCompanyId)
        );
        configsByProduct.set(product.id, {
          configs,
          files: collectProductFiles(product),
        });
        next.set(product.supplierCompanyId, configsByProduct);
        hasUnpackedConfigs = true;
      });

      if (!hasUnpackedConfigs) {
        return productConfigsBySupplier;
      }

      set({ productConfigsBySupplier: next });

      return next;
    },

    cacheRecommendedProducts: (product, showId) =>
      get().setFromProducts(
        (product.recommendedProducts ?? [])
          .map(({ recommendedProduct }) => recommendedProduct)
          .filter((recommended): recommended is IProduct => !!recommended),
        showId
      ),

    loadCustomConfigs: async ({ productId, supplierCompanyId, showId }) => {
      const rows = await customProductOptionConfigRepository.getEnabledCustomConfigsForShow(
        { productId, supplierCompanyId, showId }
      );
      const customConfigs = rows.map((config) =>
        parseCustomProductOptionConfig(config, showId)
      );

      const { productConfigsBySupplier } = get();
      const current = productConfigsBySupplier
        .get(supplierCompanyId)
        ?.get(productId);
      const configsByProduct: CustomProductOptionConfigsByProduct = new Map(
        productConfigsBySupplier.get(supplierCompanyId)
      );
      configsByProduct.set(productId, {
        configs: [
          ...(current?.configs ?? []).filter(
            ({ type }) => type === CustomProductOptionConfigType.DEFAULT
          ),
          ...customConfigs,
        ],
        files: current?.files ?? [],
      });
      const next: ProductConfigsBySupplier = new Map(productConfigsBySupplier);
      next.set(supplierCompanyId, configsByProduct);
      set({ productConfigsBySupplier: next });

      return customConfigs;
    },

    appendCustomConfigs: ({ productId, supplierCompanyId, configs }) => {
      const { productConfigsBySupplier } = get();
      const current = productConfigsBySupplier
        .get(supplierCompanyId)
        ?.get(productId);
      const storedIds = new Set(current?.configs.map(({ id }) => id));
      const configsByProduct: CustomProductOptionConfigsByProduct = new Map(
        productConfigsBySupplier.get(supplierCompanyId)
      );
      configsByProduct.set(productId, {
        configs: [
          ...(current?.configs ?? []),
          ...configs.filter(({ id }) => !storedIds.has(id)),
        ],
        files: current?.files ?? [],
      });
      const next: ProductConfigsBySupplier = new Map(productConfigsBySupplier);
      next.set(supplierCompanyId, configsByProduct);
      set({ productConfigsBySupplier: next });
    },

    reset: () => {
      set({ productConfigsBySupplier: new Map() });
    },
  }))
);

export const useCustomProductOptionStore = <
  Return = Partial<CustomProductOptionShopStore>
>(
  selector: (state: CustomProductOptionShopStore) => Return
) => customProductOptionShopStore(selector, shallow);

registerStore(customProductOptionShopStore);
